// Universo por fase, cotas e seleção de cascatas do fluxo MAD (COT-10/11) — espelho
// de normal/quotas.ts adaptado ao MAD: (a) `userCodEtapa`→`madUserCodEtapa` (composição
// integral pela dupla, sem verifyMad condicional — NIV-02/03); (b) universo restrito
// ao pool exclusivamente MAD (`isMadTask`, COT-10). COT-11 (a dupla conta como UM) é
// natural: os filtros olham status/mult/stage da task, nunca quem agiu. PARIDADE
// ESTRITA com o espelho normal preservada (bugs conhecidos anotados). Nenhum import
// de React (AD-002) nem de normal/ (isolamento AD-001).

import { MadRuleContext, isMadTask, madUserCodEtapa } from './context';
import {
  canReset,
  getPlayerStep,
  getTaskStepLocal,
  hasTaskFeedbackForUser,
  isCurrentEvaluator,
  taskAppreciationExpired,
} from './rules';

// Cópia local de utils/helpers.clamp (o módulo global carrega JSX; regras não
// importam React).
const clamp = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max);

// Universo da fase selecionada, restrito ao pool exclusivamente MAD (COT-10) — port
// de index.tsx:3458-3494 com o filtro de tab MAD (isMadOrNonEvaluator) substituído
// pelo critério canônico A2 `isMadTask` (mad === true). Sem os filtros de tab
// diretoria/rede (COT-02): no fluxo separado a divisão vira divisão por aggregate.
export function filterStageData(ctx: MadRuleContext, data: any[] | undefined): any[] {
  const playerInfo: any = ctx.playerInfo;
  const activeStageObj: any = ctx.activeStageObj;

  let stageData: any = data?.filter(item => {
    return (
      isMadTask(item) &&
      !!activeStageObj &&
      item.created >= activeStageObj?.date_start &&
      item.created <= activeStageObj?.date_finish
    );
  });

  if (activeStageObj?.type && activeStageObj?.type === 'semester') {
    stageData = stageData?.filter((item: any) => {
      const taskStep = getTaskStepLocal(item);
      return (
        (taskStep !== undefined && taskStep >= 5) ||
        getPlayerStep(ctx, item) >= 5 ||
        (Array.isArray(playerInfo?.mad_dupla) &&
          playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(item.original_manager)) &&
          madUserCodEtapa(ctx, null) &&
          madUserCodEtapa(ctx, null) === 5) ||
        (String(item.original_manager) === String(playerInfo._id) &&
          madUserCodEtapa(ctx, null) &&
          madUserCodEtapa(ctx, null) === 5)
      );
    });
  } else if (activeStageObj?.type && activeStageObj?.type === 'trimester') {
    stageData = stageData?.filter((item: any) => {
      const taskStep = getTaskStepLocal(item);
      return (
        (taskStep !== undefined && taskStep >= 4 && taskStep < 5) ||
        (getPlayerStep(ctx, item) >= 4 && getPlayerStep(ctx, item) < 5) ||
        (Array.isArray(playerInfo?.mad_dupla) &&
          playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(item.original_manager)) &&
          madUserCodEtapa(ctx, null) &&
          madUserCodEtapa(ctx, null) === 4) ||
        (String(item.original_manager) === String(playerInfo._id) &&
          madUserCodEtapa(ctx, null) &&
          madUserCodEtapa(ctx, null) === 4)
      );
    });
  }

  if (!stageData) stageData = [];

  return stageData;
}

export interface MadQuotas {
  maxValidTasks: number;
  maxRejectedTasks: number;
  feedbackTasks: number;
  dataWithoutProsseguir: any[];
  maxDoubledTasks: number;
  maxPromotedTasks: number;
  validTasks: any[];
  rejectedTasks: any[];
  doubledTasks: any[];
  promotedTasks: any[];
  maxValeuTasks: number;
  valeuTasks: any[];
  maxMandouBemTasks: number;
  mandouBemTasks: any[];
  maxUauTasks: number;
  uauTasks: any[];
}

// Cotas e universos por fase (COT-04..09) sobre o pool só-MAD — port de
// index.tsx:3574-3910 com madUserCodEtapa (composição integral pela dupla).
export function computeQuotas(ctx: MadRuleContext, stageData: any[]): MadQuotas {
  const { isVacationManager, getActiveEvaluationStep } = ctx;
  const playerInfo: any = ctx.playerInfo;
  const userInfo: any = ctx.userInfo;
  const activeStageObj: any = ctx.activeStageObj;

  // No MAD a composição pela dupla é integral (verifyMad sempre true).
  const isDirector = madUserCodEtapa(ctx, null) && madUserCodEtapa(ctx, null) === 5;

  // PIT-05/COT-11 — os marcadores de pit stop (prosseguir_idGestor/prosseguir_managers)
  // podem apontar para QUALQUER membro da dupla da sessão enquanto a task está parada
  // com a dupla como assessora: a exclusão das cotas cobre a dupla inteira, como
  // tasksProsseguirAssessor (mad/index.tsx) já faz. Sem dupla, degrada para o id exato.
  const isAssessorDaSessao = (id: any): boolean =>
    !!id && (String(id) === String(playerInfo._id) || ctx.duplaIds.includes(String(id)));
  const prosseguirManagersDaSessao = (task: any): boolean =>
    Array.isArray(task.prosseguir_managers) && task.prosseguir_managers.some((id: any) => isAssessorDaSessao(id));

  // COT-04 — para não-cod5, tasks paradas comigo (ou com meu par) como assessor saem
  // do universo de cotas
  let dataWithoutProsseguir = stageData;

  if (!isDirector) {
    dataWithoutProsseguir = stageData?.filter((task: any) => {
      if (isAssessorDaSessao(task.prosseguir_idGestor)) return false;

      return true;
    });
  }

  // COT-05
  const maxValidTasks = stageData.length;
  const maxRejectedTasks = stageData.length;
  const feedbackTasks = stageData.filter((task: any) => {
    return hasTaskFeedbackForUser(ctx, task);
  }).length;

  // COT-06 — base da cota de bonificação
  const currentMaxDoubledTasks = dataWithoutProsseguir.filter((task: any) => {
    const _taskStep = getTaskStepLocal(task);
    // COT-06: hora do cliente (bug preservado por paridade) — o original usa
    // `+new Date()` em vez de realDatetime nos ramos de lateralidade.
    const date = ctx.clientNow();

    if (prosseguirManagersDaSessao(task)) return false;

    // Gestor imediato apreciando realização expirada. 2026-07-23: além da task
    // pendente com o substituído, conta a pendente com a PRÓPRIA substituta — na
    // validação do gerente de equipe o próximo avaliador do Nível 1 pode ser a
    // gestora imediata (evaluator[último] = ela); sem isso a base fica 0 e o D-01
    // barra a dobra que V-02/R-04/D-03 autorizam.
    if (
      taskAppreciationExpired(ctx, task) &&
      isVacationManager &&
      (userInfo?._id === playerInfo?.gestor ||
        (Array.isArray(userInfo?.mad_dupla) &&
          userInfo?.mad_dupla?.some((md: any) => md._id && md._id === playerInfo?.gestor))) &&
      activeStageObj &&
      (isCurrentEvaluator(ctx, task) || isCurrentEvaluator(ctx, task, userInfo))
    ) {
      if (
        !getActiveEvaluationStep &&
        ((madUserCodEtapa(ctx, userInfo) === 4 && date < activeStageObj.date_evaluation_step4_start) ||
          (madUserCodEtapa(ctx, userInfo) === 5 && date < activeStageObj.date_evaluation_step5_start))
      ) {
        return true;
      } else if (
        getActiveEvaluationStep &&
        ((getActiveEvaluationStep > 3 && getActiveEvaluationStep <= 4 && madUserCodEtapa(ctx, userInfo) === 4) ||
          (getActiveEvaluationStep > 4 && madUserCodEtapa(ctx, userInfo) === 5))
      ) {
        return true;
      } else if (
        getActiveEvaluationStep &&
        madUserCodEtapa(ctx, null) === 4 &&
        date > activeStageObj.date_evaluation_step4_start &&
        (getTaskStepLocal(task) as number) < 4
      ) {
        return true;
      }

      return false;
    }

    // Gestor imediato apreciando realização expirada (bloco redundante preservado —
    // o original tem `return false` comentado com TO DO; sem match, o fluxo SEGUE)
    if (
      isVacationManager &&
      (userInfo?._id === playerInfo?.gestor ||
        (Array.isArray(userInfo?.mad_dupla) &&
          userInfo?.mad_dupla?.some((md: any) => md._id && md._id === playerInfo?.gestor))) &&
      activeStageObj &&
      (isCurrentEvaluator(ctx, task) || isCurrentEvaluator(ctx, task, userInfo))
    ) {
      if (
        !getActiveEvaluationStep &&
        ((madUserCodEtapa(ctx, userInfo) === 4 && date < activeStageObj.date_evaluation_step4_start) ||
          (madUserCodEtapa(ctx, userInfo) === 5 && date < activeStageObj.date_evaluation_step5_start))
      ) {
        return true;
      } else if (
        getActiveEvaluationStep &&
        ((getActiveEvaluationStep > 2 && getActiveEvaluationStep <= 4 && madUserCodEtapa(ctx, userInfo) === 4) ||
          (getActiveEvaluationStep > 4 && madUserCodEtapa(ctx, userInfo) === 5))
      ) {
        return true;
      }

      // TO DO (do original): está redundante com o IF anterior.
      // return false;
    }

    if (
      String(task.original_manager) === String(playerInfo._id) &&
      madUserCodEtapa(ctx, null) &&
      madUserCodEtapa(ctx, null) === 5 &&
      !!getActiveEvaluationStep
    ) {
      return true;
    }

    if (
      String(task.original_manager) === String(playerInfo._id) &&
      madUserCodEtapa(ctx, null) &&
      madUserCodEtapa(ctx, null) === 4
    ) {
      if (
        typeof getActiveEvaluationStep === 'number' &&
        typeof _taskStep === 'number' &&
        (getActiveEvaluationStep === 2 || (getActiveEvaluationStep > 2 && _taskStep > 2))
      ) {
        return true;
      }

      return false;
    }

    if (
      task.status === 'rejeitado' ||
      taskAppreciationExpired(ctx, task) ||
      (typeof _taskStep === 'number' && typeof getPlayerStep(ctx, task) === 'number' && _taskStep < getPlayerStep(ctx, task)) ||
      (typeof _taskStep === 'number' &&
        typeof getActiveEvaluationStep === 'number' &&
        isCurrentEvaluator(ctx, task) &&
        ((getActiveEvaluationStep === 2 && _taskStep < 2) ||
          (getActiveEvaluationStep === 3 && _taskStep < 3) ||
          (getActiveEvaluationStep === 4 && _taskStep < 4) ||
          (getActiveEvaluationStep === 5 && _taskStep < 5))) ||
      (madUserCodEtapa(ctx, null) &&
        madUserCodEtapa(ctx, null) === 4 &&
        String(task.original_manager) === String(playerInfo._id) &&
        !canReset(ctx, task))
    )
      return false;

    return true;
  });
  const maxDoubledTasks = currentMaxDoubledTasks.length;

  // COT-07 — cota de promoção
  const stepFourCurrentMaxDoubledTasks = currentMaxDoubledTasks.filter((task: any) => {
    const _step = getPlayerStep(ctx, task);
    return _step && _step >= 4 && getActiveEvaluationStep && getActiveEvaluationStep >= 4;
  });

  const percent = stepFourCurrentMaxDoubledTasks?.length ? 0.2 : 0.1;

  const maxPromotedTasks = currentMaxDoubledTasks.length
    ? clamp(Math.ceil(currentMaxDoubledTasks.length * percent), 1, Infinity)
    : 0;

  const validTasks = stageData.filter((item: any) => item.status !== 'rejeitado' && item.status !== 'aguardando');
  const rejectedTasks = stageData.filter((item: any) => item.status === 'rejeitado');

  // COT-08 — bonificadas atuais
  const doubledTasks = dataWithoutProsseguir.filter((task: any) => {
    if (prosseguirManagersDaSessao(task)) return false;

    if (
      String(task.original_manager) === String(playerInfo._id) &&
      madUserCodEtapa(ctx, null) &&
      madUserCodEtapa(ctx, null) === 5 &&
      !!getActiveEvaluationStep
    ) {
      return task.multiplicador && task.multiplicador > 1;
    }

    if (
      String(task.original_manager) === String(playerInfo._id) &&
      madUserCodEtapa(ctx, null) &&
      madUserCodEtapa(ctx, null) === 4
    ) {
      if (
        typeof getActiveEvaluationStep === 'number' &&
        typeof task.multiplicador === 'number' &&
        ((getActiveEvaluationStep === 2 && task.stageNumber >= 2) ||
          (getActiveEvaluationStep > 2 && task.stageNumber > 2) ||
          (getActiveEvaluationStep === 4 && task.multiplicador && task.multiplicador >= 4))
      ) {
        return true;
      }

      return false;
    }

    if (typeof getTaskStepLocal(task) === 'number' && (getTaskStepLocal(task) as number) > 2) {
      if ((getTaskStepLocal(task) as number) === 5 && getPlayerStep(ctx, task) === 5 && task.status === 'selo') {
        return true;
      }

      if ((getTaskStepLocal(task) as number) > getPlayerStep(ctx, task)) {
        return true;
      }

      if (
        getActiveEvaluationStep &&
        madUserCodEtapa(ctx, null) &&
        madUserCodEtapa(ctx, null) === 4 &&
        String(task.original_manager) === String(playerInfo._id) &&
        (getTaskStepLocal(task) as number) > getActiveEvaluationStep
      ) {
        return true;
      }

      if (task.status === 'dobrado' && (getTaskStepLocal(task) as number) === getPlayerStep(ctx, task)) {
        return true;
      }

      if (
        (task.status === 'concluidoAuto' || task.status === 'concluido') &&
        (getTaskStepLocal(task) as number) === getPlayerStep(ctx, task) &&
        (task.multiplicador === 2 || task.multiplicador === 4)
      ) {
        return true;
      }

      return false;
    }

    return false;
  });

  // COT-08 — promovidas atuais
  const promotedTasks = dataWithoutProsseguir.filter((task: any) => {
    if (prosseguirManagersDaSessao(task)) return false;

    if (
      String(task.original_manager) === String(playerInfo._id) &&
      madUserCodEtapa(ctx, null) &&
      madUserCodEtapa(ctx, null) === 5 &&
      !!getActiveEvaluationStep
    ) {
      return task.status === 'selo';
    }

    if (
      String(task.original_manager) === String(playerInfo._id) &&
      madUserCodEtapa(ctx, null) &&
      madUserCodEtapa(ctx, null) === 4
    ) {
      if (
        typeof getActiveEvaluationStep === 'number' &&
        typeof task.multiplicador === 'number' &&
        getActiveEvaluationStep > 2 &&
        task.stageNumber > 2
      ) {
        return true;
      }

      return false;
    }

    if (typeof getTaskStepLocal(task) === 'number' && (getTaskStepLocal(task) as number) > 2) {
      if ((getTaskStepLocal(task) as number) === 5 && getPlayerStep(ctx, task) === 5 && task.status === 'selo') {
        return true;
      }

      if ((getTaskStepLocal(task) as number) > getPlayerStep(ctx, task)) {
        return true;
      }

      if (
        getActiveEvaluationStep &&
        madUserCodEtapa(ctx, null) &&
        madUserCodEtapa(ctx, null) === 4 &&
        String(task.original_manager) === String(playerInfo._id) &&
        (getTaskStepLocal(task) as number) > getActiveEvaluationStep
      ) {
        return true;
      }

      return false;
    }

    return false;
  });

  // COT-09 — prêmios
  const maxValeuTasks = clamp(stageData.length, 1, Infinity);
  const valeuTasks = stageData.filter((x: any) => x.status === 'valeu');

  const maxMandouBemTasks = clamp(Math.ceil(stageData.length * 0.5), 1, Infinity);
  const mandouBemTasks = stageData.filter((x: any) => x.status === 'mandou');

  const maxUauTasks = clamp(Math.ceil(stageData.length * 0.25), 1, Infinity);
  const uauTasks = stageData.filter((x: any) => x.status === 'selo');

  return {
    maxValidTasks,
    maxRejectedTasks,
    feedbackTasks,
    dataWithoutProsseguir,
    maxDoubledTasks,
    maxPromotedTasks,
    validTasks,
    rejectedTasks,
    doubledTasks,
    promotedTasks,
    maxValeuTasks,
    valeuTasks,
    maxMandouBemTasks,
    mandouBemTasks,
    maxUauTasks,
    uauTasks,
  };
}

// CAS-01 — cascata direta: promoção que fecha a cota converte as demais `dobrado`
// do mesmo multiplicador em concluidoAuto — port de index.tsx:2357-2360 + 2374-2394.
// A cota é recalculada como no original: promovidas atuais sem a task nova + ela.
export function selectCascadeTargets(
  ctx: MadRuleContext,
  filteredData: any[],
  newObj: any,
  canResetFn: (task: any) => boolean,
): any[] {
  const playerInfo: any = ctx.playerInfo;
  const { getActiveEvaluationStep, promotedTasks, maxPromotedTasks } = ctx;

  const _promotedTasks = promotedTasks.filter((x: any) => x._id !== newObj._id);
  _promotedTasks.push(newObj);

  const cotaReached = _promotedTasks.length >= maxPromotedTasks;

  return [...(filteredData as any[])].filter((x: any) => {
    if (
      x._id !== newObj._id &&
      canResetFn(x) &&
      x.status === 'dobrado' &&
      x.multiplicador === newObj.multiplicador &&
      cotaReached &&
      !(
        getActiveEvaluationStep &&
        getActiveEvaluationStep < 4 &&
        (String(x.original_manager) === String(playerInfo._id) ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(x.original_manager)))) &&
        (madUserCodEtapa(ctx, null) === 4 || madUserCodEtapa(ctx, null) === 5)
      )
    ) {
      return true;
    }

    return false;
  });
}

// CAS-04 — cascata reversa: reset de promovido/selo devolve os concluidoAuto do
// mesmo step (da task nova OU da antiga) para dobrado — port de index.tsx:2420-2432.
export function selectCascadeReversal(
  ctx: MadRuleContext,
  filteredData: any[],
  newObj: any,
  oldObj: any,
  canResetFn: (task: any) => boolean,
): any[] {
  return [...(filteredData as any[])].filter((x: any) => {
    if (
      x._id !== newObj._id &&
      canResetFn(x) &&
      x.status === 'concluidoAuto' &&
      (getTaskStepLocal(x) === getTaskStepLocal(newObj) || getTaskStepLocal(x) === getTaskStepLocal(oldObj)) &&
      (oldObj?.status === 'promovido' || oldObj?.status === 'selo')
    ) {
      return true;
    }

    return false;
  });
}
